import { useState, useMemo, useRef, useEffect } from "react";
import { FiClock, FiChevronDown, FiInfo } from "react-icons/fi";
import ReactTooltip from "react-tooltip";
import dayjs from "newui/utils/dayjs";
import ReactSelect from "react-select";
import makeAnimated from "react-select/animated";
import { FormLabel } from "../FormLabel";
import FormikFieldError from "../FormikFieldError";
import { Popover } from "components/Popover/Popover";
import { ClockPicker } from "./ClockPicker";
import { getInputClasses, getInputWrapperClasses } from "./inputStyles";
import InfoTooltip from "newui/components/InfoTooltip";
import { TimeZonePickerWithLabel } from "./TimeZonePickerWithLabel";

const animatedComponents = makeAnimated();

export interface BaseTimePickerWithLabelProps {
  name: string;
  className?: string;
  disabled?: boolean;
  errorText?: string;
  guideLine?: string;
  hasError?: boolean;
  label?: string;
  nameTimezone: string;
  required?: boolean;
  restrictTimezones?: string;
  testId?: string;
  timezone?: string;
  value?: string;
  onTimeChange?: (time: string) => void;
  onTimeZoneChange?: (timezone: string) => void;
}

export interface Time12 { hour: string; minute: string; period: 'AM' | 'PM' };

export interface Time24 { hour: string; hourNum: number; minute: string; minuteNum: number; };

/**
 * Parse time string (24h or 12h) into 12-hour format
 */
export const parseTime = (timeStr: string): Time12 => {
  // Match 24-hour "HH:MM" or 12-hour "HH:MM AM/PM"
  const match = timeStr.match(/^(\d{1,2}):(\d{2})(?:\s?(AM|PM))?$/i);
  if (!match) return { hour: '12', minute: '00', period: 'AM' };

  const [_, h, m, p] = match;
  let hour = parseInt(h, 10);
  const minute = m;
  let period: 'AM' | 'PM' = (p?.toUpperCase() as 'AM' | 'PM') || 'AM';

  if (!period) {
    // 24-hour format
    if (hour === 0) {
      hour = 12;
      period = 'AM';
    } else if (hour === 12) {
      period = 'PM';
    } else if (hour > 12) {
      hour -= 12;
      period = 'PM';
    } else {
      period = 'AM';
    }
  }

  return {
    hour: hour.toString().padStart(2, '0'),
    minute,
    period,
  };
};

export const time12To24 = ({ hour, minute, period }: Time12): Time24 => {
  let hr = parseInt(hour, 10);

  if (period === 'AM' && hr === 12) {
    hr = 0;
  }

  if (period === 'PM' && hr < 12) {
    hr += 12;
  }

  return {
    hour: hour.toString().padStart(2, '0'),
    minute,
    hourNum: parseInt(hour),
    minuteNum: parseInt(minute, 10),
  };
};

export const TimePickerWithLabel: React.FC<BaseTimePickerWithLabelProps> = ({
  className = "",
  disabled,
  errorText,
  guideLine,
  hasError = false,
  label,
  name,
  nameTimezone,
  required,
  restrictTimezones = "Australia",
  testId,
  value = "",
  timezone = dayjs.tz.guess(),
  onTimeChange,
  onTimeZoneChange,
}) => {
  const [showClockPicker, setShowClockPicker] = useState(false);
  const [showTimezonePicker, setShowTimezonePicker] = useState(false);
  const [showMobilePicker, setShowMobilePicker] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // Track unpadded partial input while the user is mid-type (e.g. "3" before second digit)
  const [pendingHour, setPendingHour] = useState<string | null>(null);
  const [pendingMinute, setPendingMinute] = useState<string | null>(null);
  const clockIconRef = useRef<HTMLDivElement>(null);
  const timezoneRef = useRef<HTMLDivElement>(null);
  const hasInputError = !!errorText || hasError;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { hour, minute, period } = parseTime(value);

  // Show pending (unpadded) partial while typing, fall back to committed value
  const displayHour = pendingHour !== null ? pendingHour : hour;
  const displayMinute = pendingMinute !== null ? pendingMinute : minute;

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      return;
    }
    // Sliding window: if more than 2 digits, take the last 2
    const newHour = raw.length > 2 ? raw.slice(-2) : raw;
    const hourNum = parseInt(newHour, 10);

    if (newHour.length === 2) {
      if (hourNum < 1 || hourNum > 12) {
        return; // invalid — reject silently, keep current display
      }
      setPendingHour(null);
      if (onTimeChange) {
        onTimeChange(`${newHour}:${minute} ${period}`);
      }
    } else {
      // Single digit partial: store unpadded so next keystroke slides correctly
      setPendingHour(newHour);
    }
  };

  const handleHourBlur = () => {
    if (pendingHour !== null) {
      const hourNum = parseInt(pendingHour, 10);
      if (hourNum >= 1 && hourNum <= 9) {
        if (onTimeChange) {
          onTimeChange(`0${pendingHour}:${minute} ${period}`);
        }
      }
      setPendingHour(null);
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      return;
    }
    const newMinute = raw.length > 2 ? raw.slice(-2) : raw;
    const minuteNum = parseInt(newMinute, 10);

    if (newMinute.length === 2) {
      if (minuteNum > 59) {
        return; // invalid — reject silently
      }
      setPendingMinute(null);
      if (onTimeChange) {
        onTimeChange(`${hour}:${newMinute} ${period}`);
      }
    } else {
      setPendingMinute(newMinute);
    }
  };

  const handleMinuteBlur = () => {
    if (pendingMinute !== null) {
      const minuteNum = parseInt(pendingMinute, 10);
      if (!isNaN(minuteNum)) {
        if (onTimeChange) {
          onTimeChange(`${hour}:0${pendingMinute} ${period}`);
        }
      }
      setPendingMinute(null);
    }
  };

  const handleHourKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
      return;
    }
    e.preventDefault();
    if (disabled) {
      return;
    }

    // Arrow key commits any pending partial and operates on the displayed value
    const baseHour = pendingHour !== null ? pendingHour : hour;
    setPendingHour(null);
    let hourNum = parseInt(baseHour, 10);
    if (isNaN(hourNum) || hourNum < 1 || hourNum > 12) {
      hourNum = parseInt(hour, 10);
    }
    let newPeriod = period;

    if (e.key === 'ArrowUp') {
      if (hourNum === 11) {
        hourNum = 12;
        newPeriod = period === 'AM' ? 'PM' : 'AM';
      } else if (hourNum === 12) {
        hourNum = 1;
      } else {
        hourNum += 1;
      }
    } else {
      if (hourNum === 12) {
        hourNum = 11;
        newPeriod = period === 'AM' ? 'PM' : 'AM';
      } else if (hourNum === 1) {
        hourNum = 12;
      } else {
        hourNum -= 1;
      }
    }

    if (onTimeChange) {
      onTimeChange(`${String(hourNum).padStart(2, '0')}:${minute} ${newPeriod}`);
    }
  };

  const handleMinuteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
      return;
    }
    e.preventDefault();
    if (disabled) {
      return;
    }

    const baseMinute = pendingMinute !== null ? pendingMinute : minute;
    setPendingMinute(null);
    let minuteNum = parseInt(baseMinute, 10);
    if (isNaN(minuteNum)) {
      minuteNum = 0;
    }

    if (e.key === 'ArrowUp') {
      minuteNum = minuteNum === 59 ? 0 : minuteNum + 1;
    } else {
      minuteNum = minuteNum === 0 ? 59 : minuteNum - 1;
    }

    if (onTimeChange) {
      onTimeChange(`${hour}:${String(minuteNum).padStart(2, '0')} ${period}`);
    }
  };

  const handlePeriodChange = (option: any) => {
    const newPeriod = option?.value || 'AM';
    const formattedTime = `${hour}:${minute} ${newPeriod}`;
    if (onTimeChange) {
      onTimeChange(formattedTime);
    }
  };

  const handleClockPickerChange = (time: string) => {
    if (onTimeChange) {
      onTimeChange(time);
    }
  };

  const displayTimezone = timezone ? timezone.split('/').pop()?.replace(/_/g, ' ') : "Timezone";

  const periodOptions = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' },
  ];

  const selectedPeriod = periodOptions.find(opt => opt.value === period) || periodOptions[0];

  if (isMobile) {
    return (
      <div className={getInputWrapperClasses(hasInputError, false, className)}>
        {label && (
          <FormLabel htmlFor={name} label={label} required={required}>
            {guideLine && (
              <div className="inline-block">
                <FiInfo
                  className="cursor-pointer ml-1"
                  size={16}
                  data-tip
                  data-for={name}
                />
                <ReactTooltip
                  id={name}
                  place="right"
                  effect="solid"
                  className="!border !border-gray-400 !rounded !text-xs !normal-case !px-2 !py-1 !font-normal !max-w-md"
                  arrowColor="transparent"
                >
                  {guideLine}
                </ReactTooltip>
              </div>
            )}
          </FormLabel>
        )}

        <div
          className={getInputClasses(hasInputError, "h-14 max-h-14 flex items-center justify-between cursor-pointer")}
          style={{
            backgroundColor: disabled ? "#f9f9f9" : "white",
          }}
          data-testid={testId}
          onClick={() => !disabled && setShowMobilePicker(true)}
        >
          <div className="flex items-center gap-2 flex-1">
            <FiClock className="text-blue-500" size={20} />
            <span className="text-gray-700">
              {hour}:{minute} {period}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 border-l border-gray-200">
            <span className="text-gray-700 text-sm">{displayTimezone}</span>
            <FiChevronDown className="text-gray-400" size={16} />
          </div>
        </div>

        {showMobilePicker && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setShowMobilePicker(false)}
            />
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md">
              <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4">
                <ClockPicker
                  value={value}
                  onChange={handleClockPickerChange}
                  onComplete={() => setShowMobilePicker(false)}
                />

                <div className="border-t border-gray-200 pt-4 mt-2">
                  <TimeZonePickerWithLabel
                    name={nameTimezone}
                    label="Timezone"
                    value={timezone}
                    onChange={onTimeZoneChange}
                    disabled={disabled}
                    restrictTo={restrictTimezones}
                  />
                </div>

                <div className="border-t border-gray-200 pt-4 mt-2">
                  <button
                    type="button"
                    onClick={() => setShowMobilePicker(false)}
                    className="w-full py-3 bg-blue-500 text-white rounded-md font-medium hover:bg-blue-600 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        <FormikFieldError text={errorText} />
      </div>
    );
  }

  return (
    <div className={getInputWrapperClasses(hasInputError, false, className)}>
      {label && (
        <FormLabel htmlFor={name} label={label} required={required}>
          {guideLine && (
            <div className="inline-block">
              <InfoTooltip tooltipText={guideLine} />
            </div>
          )}
        </FormLabel>
      )}

      <div
        className={getInputClasses(hasInputError, "h-14 max-h-14 flex items-center gap-2 px-3")}
        style={{
          backgroundColor: disabled ? "#f9f9f9" : "white",
        }}
        data-testid={testId}
      >
        <input
          type="text"
          name={name}
          value={displayHour}
          onChange={handleHourChange}
          onKeyDown={handleHourKeyDown}
          onBlur={handleHourBlur}
          onFocus={(e) => { setPendingHour(null); e.target.select(); }}
          disabled={disabled}
          placeholder="HH"
          maxLength={2}
          className="w-10 text-center outline-none bg-transparent text-gray-700 disabled:cursor-not-allowed"
        />
        <span className="text-gray-400">:</span>
        <input
          type="text"
          value={displayMinute}
          onChange={handleMinuteChange}
          onKeyDown={handleMinuteKeyDown}
          onBlur={handleMinuteBlur}
          onFocus={(e) => { setPendingMinute(null); e.target.select(); }}
          disabled={disabled}
          placeholder="mm"
          maxLength={2}
          className="w-10 text-center outline-none bg-transparent text-gray-700 disabled:cursor-not-allowed"
        />

        <div className="w-20 ml-1 flex items-center">
          <ReactSelect
            unstyled
            classNames={{
              container: () => "",
              control: () => "px-2 py-1 border border-gray-200 rounded hover:border-blue-300 focus:border-blue-400 min-h-0",
              valueContainer: () => "py-0",
              input: () => "text-gray-700",
              placeholder: () => "text-gray-400",
              singleValue: () => "text-gray-700",
              menu: () => "mt-1 border border-gray-200 rounded-md bg-white shadow-lg z-50",
              menuList: () => "py-1",
              option: (state: any) => `px-3 py-2 cursor-pointer ${state.isSelected
                ? 'bg-blue-500 text-white'
                : state.isFocused
                  ? 'bg-blue-50 text-gray-700'
                  : 'text-gray-700'
                }`,
              dropdownIndicator: () => "text-gray-400 p-0",
              indicatorSeparator: () => "hidden",
            }}
            components={animatedComponents}
            onChange={handlePeriodChange}
            value={selectedPeriod}
            options={periodOptions}
            isDisabled={disabled}
            isSearchable={false}
          />
        </div>

        <Popover
          isOpen={showClockPicker}
          closeHandler={() => setShowClockPicker(false)}
          openHandler={() => !disabled && setShowClockPicker(true)}
          placement="bottom-start"
          offset={[0, 4]}
          keyFor={`${name}-clock`}
        >
          <div
            ref={clockIconRef}
            className="flex items-center justify-center w-8 h-8 cursor-pointer hover:bg-blue-50 rounded transition-colors ml-1"
            onClick={() => !disabled && setShowClockPicker(true)}
          >
            <FiClock className="text-blue-500" size={20} />
          </div>

          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-[320px]">
            <ClockPicker
              value={value}
              onChange={handleClockPickerChange}
            />
          </div>
        </Popover>

        <Popover
          isOpen={showTimezonePicker}
          closeHandler={() => setShowTimezonePicker(false)}
          openHandler={() => !disabled && setShowTimezonePicker(true)}
          placement="bottom-end"
          offset={[0, 4]}
          keyFor={`${name}-timezone`}
        >
          <div
            ref={timezoneRef}
            className="flex items-center gap-2 px-3 py-2 border-l border-gray-200 cursor-pointer hover:bg-blue-50 rounded transition-colors ml-auto"
            onClick={() => !disabled && setShowTimezonePicker(true)}
          >
            <span className="text-gray-700 text-sm whitespace-nowrap">{displayTimezone}</span>
            <FiChevronDown className="text-gray-400" size={16} />
          </div>

          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-[280px]">
            <TimeZonePickerWithLabel
              name={nameTimezone}
              label="Timezone"
              value={timezone}
              onChange={onTimeZoneChange}
              disabled={disabled}
              restrictTo={restrictTimezones}
            />
          </div>
        </Popover>
      </div>

      <FormikFieldError text={errorText} />
    </div>
  );
};
